import { IntegrationProvider } from "@repo/app-types/entities/integration";
import { Link } from "@repo/ui/Link/index";
import type { FC } from "react";
import { appPaths } from "../../../app/appPaths.js";
import {
  checkAlpharunOperatedIntegrationEnabled,
  useAlpharunOperatedIntegrationDetailOutletContext,
} from "./alpharunOperatedIntegrationDetailSection.js";

/**
 * The Settings tab of an Alpharun-operated integration. Nothing is editable or
 * shown beyond the pause state: Alpharun holds the credentials and agent list
 * and changes them on request.
 */
export const AlpharunOperatedIntegrationSettingsTab: FC = () => {
  const { integration, config } =
    useAlpharunOperatedIntegrationDetailOutletContext();

  return (
    <div className="max-w-3xl space-y-6">
      <p className="text-sm leading-6 text-muted-foreground">
        {config.description}
      </p>

      {!checkAlpharunOperatedIntegrationEnabled(integration) ? (
        <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
          Imports are currently paused by Alpharun.
        </div>
      ) : null}

      {integration.provider === IntegrationProvider.API_RECORDING_UPLOAD ? (
        <>
          <p className="text-sm leading-6 text-muted-foreground">
            Recordings uploaded with{" "}
            <code className="font-mono text-xs">/recordings/upload</code> or{" "}
            <code className="font-mono text-xs">/recordings/upload-video</code>{" "}
            are processed right away and don&apos;t appear here.
          </p>
          <p className="text-sm leading-6 text-muted-foreground">
            This integration was created by your first recording import and has
            nothing to configure. Imports are authenticated with your{" "}
            <Link href={appPaths.settingsDevelopers.build()} sameTab>
              API keys
            </Link>
            .
          </p>
        </>
      ) : (
        <p className="text-sm leading-6 text-muted-foreground">
          This integration is managed by Alpharun. To change which agents are
          imported or update your {config.name} credentials, contact your
          Alpharun team.
        </p>
      )}
    </div>
  );
};
